import { useEffect, useState } from "react";
import axios from "axios";
import { EnterpriseLayout } from "@/components/layout/EnterpriseLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DollarSign, CheckCircle, XCircle, Clock, Phone, User, ExternalLink, Zap, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const EnterpriseWithdrawals = () => {
    const [withdrawals, setWithdrawals] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchWithdrawals();
    }, []);

    const fetchWithdrawals = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/marketplace/admin/withdrawals`);
            setWithdrawals(response.data);
        } catch (error) {
            console.error("Error fetching withdrawals:", error);
            toast.error("Pa kapab chaje demann retrè yo.");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (id, newStatus) => {
        try {
            await axios.post(`${API}/marketplace/admin/withdrawals/${id}/status`, { status: newStatus });
            toast.success(`Demann nan mete kòm ${newStatus.toUpperCase()}`);
            fetchWithdrawals(); 
        } catch (error) {
            console.error("Error updating withdrawal:", error);
            toast.error("Echèk nan aktyalizasyon demann nan.");
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('fr-HT', {
            style: 'decimal',
            minimumFractionDigits: 0,
        }).format(price);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleDateString('fr-HT', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const getStatusBadge = (status) => {
        const config = {
            paid: { class: "bg-green-600 text-white shadow-green-100", label: "Peye", icon: CheckCircle },
            approved: { class: "bg-blue-600 text-white shadow-blue-100", label: "Apwouve", icon: Zap },
            rejected: { class: "bg-red-500 text-white shadow-red-100", label: "Rejte", icon: XCircle },
            pending: { class: "bg-amber-500 text-white shadow-amber-100", label: "Atant", icon: Clock }
        };
        const st = config[status] || { class: "bg-gray-500 text-white", label: status, icon: Clock };
        return (
            <span className={`px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg ${st.class}`}>
                <st.icon className="w-3 h-3" /> {st.label}
            </span>
        );
    };

    return (
        <EnterpriseLayout>
            <div className="space-y-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h1 className="text-4xl font-black text-[#232F3E] tracking-tighter italic uppercase leading-none">
                            Demann <span className="text-blue-600">Retrè</span>
                        </h1>
                        <p className="text-gray-400 font-bold uppercase tracking-widest text-[9px] mt-2">
                            Peye machann yo kòb yo vann sou Marketplace la nan men kliyan yo.
                        </p>
                    </div>
                    
                    <div className="flex gap-4">
                        <div className="bg-white px-8 py-5 rounded-[25px] flex items-center gap-6 shadow-2xl shadow-gray-100 border border-gray-50">
                            <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600">
                                <DollarSign className="w-6 h-6" />
                            </div>
                            <div>
                                <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest">Demann Total</p>
                                <p className="text-3xl font-black text-gray-900 tracking-tighter">{withdrawals.length}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-32">
                        <div className="w-14 h-14 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <Card className="border-0 shadow-[0_50px_100px_-20px_rgba(37,99,235,0.1)] rounded-[45px] overflow-hidden bg-white">
                        <CardContent className="p-0">
                            {withdrawals.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-gray-50/50 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] border-b border-gray-100/50">
                                                <th className="py-8 px-10 text-left">Machann / Kontak</th>
                                                <th className="py-8 px-8 text-left">Montan Retrè</th>
                                                <th className="py-8 px-8 text-left">Metòd Peman</th>
                                                <th className="py-8 px-8 text-left">Detay Kont</th>
                                                <th className="py-8 px-8 text-left">Estati</th>
                                                <th className="py-8 px-10 text-center">Aksyon</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {withdrawals.map((w) => (
                                                <tr key={w.id} className="hover:bg-gray-50/80 transition-all border-b border-gray-50 last:border-0 group">
                                                    <td className="py-8 px-10">
                                                        <div className="flex items-center gap-6">
                                                            <div className="w-14 h-14 bg-gray-50 rounded-[22px] flex items-center justify-center text-gray-300 font-black text-2xl group-hover:scale-110 transition-transform">
                                                                {(w.seller_name || "?")[0].toUpperCase()}
                                                            </div>
                                                            <div className="flex flex-col">
                                                                <span className="font-black text-gray-900 text-lg tracking-tight group-hover:text-blue-600 transition-colors">{w.seller_name}</span>
                                                                <span className="text-[11px] text-gray-400 font-black flex items-center gap-1 uppercase tracking-widest mt-1">
                                                                    <Phone className="w-3.5 h-3.5 text-blue-500" /> {w.seller_phone}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="py-8 px-8">
                                                        <div className="flex flex-col">
                                                            <span className="text-2xl font-black text-emerald-600 italic tracking-tighter leading-none">{formatPrice(w.amount_htg)} <span className="text-[10px] text-gray-400 not-italic ml-0.5">HTG</span></span>
                                                            <span className="text-[9px] text-gray-300 font-bold uppercase tracking-widest mt-2">Balans Marketplace</span>
                                                        </div>
                                                    </td>
                                                    <td className="py-8 px-8">
                                                        <span className="px-4 py-2 rounded-xl bg-blue-50 border border-blue-100 text-[10px] font-black text-blue-600 uppercase tracking-widest italic shadow-sm">
                                                            {w.payment_method}
                                                        </span>
                                                    </td>
                                                    <td className="py-8 px-8">
                                                        <div className="flex flex-col gap-1">
                                                            <span className="text-sm font-black font-mono text-gray-700 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100 group-hover:bg-white group-hover:border-blue-200 transition-colors">{w.payment_details}</span>
                                                            <span className="text-[9px] text-gray-300 font-bold uppercase tracking-widest pl-1 mt-1">Details Verified</span>
                                                        </div>
                                                    </td>
                                                    <td className="py-8 px-8">
                                                        {getStatusBadge(w.status)}
                                                    </td>
                                                    <td className="py-8 px-10 text-center">
                                                        <div className="flex items-center justify-center gap-4">
                                                            {w.status === 'pending' && (
                                                                <>
                                                                    <Button 
                                                                        size="sm" 
                                                                        className="bg-blue-600 hover:bg-black text-white rounded-2xl text-[10px] font-black uppercase tracking-widest h-12 px-6 shadow-xl shadow-blue-100 transition-all hover:scale-105 active:scale-95"
                                                                        onClick={() => handleUpdateStatus(w.id, 'approved')}
                                                                    >
                                                                        <Zap className="w-4 h-4 mr-2" /> Apouve
                                                                    </Button>
                                                                    <Button 
                                                                        variant="ghost" 
                                                                        size="sm" 
                                                                        className="text-red-500 hover:bg-red-50 rounded-2xl text-[10px] font-black uppercase tracking-widest h-12 px-6 border border-red-50 transition-all hover:border-red-500"
                                                                        onClick={() => handleUpdateStatus(w.id, 'rejected')}
                                                                    >
                                                                        <AlertCircle className="w-4 h-4 mr-2" /> Rejte
                                                                    </Button>
                                                                </>
                                                            )}
                                                            {w.status === 'approved' && (
                                                                <Button 
                                                                    size="sm" 
                                                                    className="bg-green-600 hover:bg-black text-white rounded-2xl text-[10px] font-black uppercase tracking-widest h-12 px-8 shadow-xl shadow-green-100 transition-all hover:scale-105 active:scale-95"
                                                                    onClick={() => handleUpdateStatus(w.id, 'paid')}
                                                                >
                                                                    <CheckCircle className="w-4 h-4 mr-2" /> Make kòm Peye
                                                                </Button>
                                                            )}
                                                            {(w.status === 'paid' || w.status === 'rejected') && (
                                                                <div className="text-gray-200 opacity-20 group-hover:opacity-100 transition-opacity">
                                                                    <CheckCircle className="w-8 h-8 mx-auto" strokeWidth={3} />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-48 text-gray-400 flex flex-col items-center">
                                    <Clock className="w-24 h-24 mb-8 opacity-5 italic" />
                                    <p className="font-black uppercase tracking-widest text-[11px] max-w-xs leading-loose">Pa gen okenn demann retrè atant pou kounye a.</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>
        </EnterpriseLayout>
    );
};
